/**
 * Theme context for managing light/dark mode and muted/colorful styles
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  ThemeMode,
  ThemeBrightness,
  ThemeStyle,
  ThemeConfig,
  ThemeVariantConfig,
  initializeTheme,
  setThemeBrightness as setGlobalThemeBrightness,
  setThemeMode as setGlobalThemeMode,
  getCurrentTheme,
  toggleBrightness as toggleGlobalBrightness,
  toggleStyle as toggleGlobalStyle,
  isDarkMode,
  isColorfulStyle,
  generateThemeCSSVariables,
  generateCSSVariables,
  setBrandConfig,
  sampleBrandConfigs,
} from '@restaurant/core';
import { safeGetLogger } from '@restaurant/logging';
import { getShowcaseThemeMode } from '../showcase/customize';


interface ThemeContextValue {
  theme: ThemeConfig | null;
  brightness: ThemeBrightness;
  style: ThemeStyle;
  mode: ThemeMode;
  isDark: boolean;
  isColorful: boolean;
  isInitialized: boolean;
  setBrightness: (brightness: ThemeBrightness) => void;
  setMode: (mode: ThemeMode) => void;
  setTheme: (restaurant: keyof typeof sampleBrandConfigs, mode: ThemeMode) => void;
  toggleBrightness: () => void;
  toggleStyle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  initialBrightness?: ThemeBrightness;
  variantConfig?: Partial<ThemeVariantConfig>;
}

export function ThemeProvider({
  children,
  initialBrightness = 'light',
  variantConfig,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeConfig | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize theme system
  useEffect(() => {
    try {
      initializeTheme({
        brightness: initialBrightness,
        variantConfig,
      });
      
      // Get the initialized theme
      const initialTheme = getCurrentTheme();
      setThemeState(initialTheme);
      setIsInitialized(true);
    } catch (error) {
      safeGetLogger()?.error('Theme initialization failed', { domain: 'config', error: String(error) });
      // Set a fallback theme
      setThemeState({
        mode: 'mutedlight',
        brightness: 'light',
        style: 'muted'
      } as ThemeConfig);
      setIsInitialized(true);
    }
  }, [initialBrightness, variantConfig]);

  // Update theme state when global theme changes
  const updateTheme = useCallback(() => {
    try {
      const newTheme = getCurrentTheme();
      setThemeState(newTheme);
    
    // Apply theme CSS variables
    const styleElement = document.getElementById('theme-variables') || createStyleElement();
    styleElement.textContent = generateThemeCSSVariables();
    
    // Update brand CSS variables (including prominent colors)
    const updateBrandTokens = () => {
      const brandCSSVars = generateCSSVariables();
      let brandStyleElement = document.getElementById('brand-tokens');
      if (!brandStyleElement) {
        brandStyleElement = document.createElement('style');
        brandStyleElement.id = 'brand-tokens';
        document.head.appendChild(brandStyleElement);
      }
      brandStyleElement.textContent = brandCSSVars;
    };
    updateBrandTokens();
    
    // Set data-theme attribute on document
    document.documentElement.setAttribute('data-theme', newTheme.mode);
    document.documentElement.setAttribute('data-brightness', newTheme.brightness);
    document.documentElement.setAttribute('data-style', newTheme.style);
    
    // Store preference in localStorage
    localStorage.setItem('theme-brightness', newTheme.brightness);
    } catch (error) {
      safeGetLogger()?.error('Theme update failed', { domain: 'config', error: String(error) });
    }
  }, []);

  // Create style element for theme variables if it doesn't exist
  const createStyleElement = () => {
    const style = document.createElement('style');
    style.id = 'theme-variables';
    document.head.appendChild(style);
    return style;
  };

  // Force colorful light mode while theme system is being hardened.
  // TODO: Restore user/system preference support once all four theme
  // variants (mutedlight, muteddark, colorfullight, colorfuldark) are
  // visually verified across desktop and mobile.
  // Showcase carve-out (round-2 customizer): the outreach preview initializes
  // from the lead's live selection instead — real tenants keep the force.
  useEffect(() => {
    const isShowcase = document.documentElement.dataset.showcase === '1';
    setGlobalThemeMode(isShowcase ? getShowcaseThemeMode() : 'colorfullight');
    updateTheme();
  }, [updateTheme]);

  const setBrightness = useCallback((brightness: ThemeBrightness) => {
    setGlobalThemeBrightness(brightness);
    updateTheme();
  }, [updateTheme]);

  const setMode = useCallback((mode: ThemeMode) => {
    setGlobalThemeMode(mode);
    updateTheme();
  }, [updateTheme]);

  const toggleBrightness = useCallback(() => {
    toggleGlobalBrightness();
    updateTheme();
  }, [updateTheme]);

  const toggleStyle = useCallback(() => {
    toggleGlobalStyle();
    updateTheme();
  }, [updateTheme]);

  const setTheme = useCallback((restaurant: keyof typeof sampleBrandConfigs, mode: ThemeMode) => {
    setBrandConfig(restaurant);
    setGlobalThemeMode(mode);
    updateTheme();
  }, [updateTheme]);

  const value: ThemeContextValue = {
    theme,
    brightness: theme?.brightness || 'light',
    style: theme?.style || 'muted',
    mode: theme?.mode || 'mutedlight',
    isDark: theme ? isDarkMode() : false,
    isColorful: theme ? isColorfulStyle() : false,
    isInitialized,
    setBrightness,
    setMode,
    setTheme,
    toggleBrightness,
    toggleStyle,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}